import { useCallback, useEffect, useState } from "react";

// Mirrors core.WebContest (GET /v1/web/contests). Money arrives as integer
// cents plus a display string; the page shows the string and never derives
// a pool, a payout, or a fee from the cents.
export type WebMoney = {
  kind: "pool" | "cash" | "prize" | "none" | "fee" | "free" | "pay_as_you_go";
  cents?: number;
  display?: string;
  take_bps?: number;
  label?: string;
};

export type WebContest = {
  slug: string;
  title: string;
  host_username?: string;
  challenge?: string;
  genre?: string;
  duration?: string;
  format: "standard" | "versus";
  state: "open" | "judging" | "complete";
  ends_at: string;
  seats_total: number;
  seats_taken: number;
  cover_url?: string;
  preview_url?: string;
  prize: WebMoney;
  admission: WebMoney;
  has_sample_pack: boolean;
  is_seeded: boolean;
  winner_username?: string;
  viewer?: { entered?: boolean; sponsor?: boolean };
};

export type ContestsState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; contests: WebContest[] };

const CONTESTS_URL = "/v1/web/contests";

const str = (v: unknown): string | undefined => (typeof v === "string" && v !== "" ? v : undefined);
const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);

function money(raw: unknown): WebMoney {
  const m = (raw ?? {}) as Record<string, unknown>;
  return {
    kind: (str(m.kind) ?? "none") as WebMoney["kind"],
    cents: typeof m.cents === "number" ? m.cents : undefined,
    display: str(m.display),
    take_bps: typeof m.take_bps === "number" ? m.take_bps : undefined,
    label: str(m.label),
  };
}

// Keep only the fields the page uses, so nothing extra in the payload can
// reach state or the DOM.
export function normalizeContests(raw: unknown): WebContest[] {
  const list = (raw as { contests?: unknown } | null)?.contests;
  if (!Array.isArray(list)) return [];
  return list
    .filter((c): c is Record<string, unknown> => c !== null && typeof c === "object" && typeof c.slug === "string")
    .map((c) => {
      const viewer = c.viewer as { entered?: unknown; sponsor?: unknown } | undefined;
      return {
        slug: c.slug as string,
        title: str(c.title) ?? (c.slug as string),
        host_username: str(c.host_username),
        challenge: str(c.challenge),
        genre: str(c.genre),
        duration: str(c.duration),
        format: c.format === "versus" ? "versus" : "standard",
        state: c.state === "judging" || c.state === "complete" ? c.state : "open",
        ends_at: str(c.ends_at) ?? "",
        seats_total: num(c.seats_total),
        seats_taken: num(c.seats_taken),
        cover_url: str(c.cover_url),
        preview_url: str(c.preview_url),
        prize: money(c.prize),
        admission: money(c.admission),
        has_sample_pack: c.has_sample_pack === true,
        is_seeded: c.is_seeded === true,
        winner_username: str(c.winner_username),
        viewer: viewer ? { entered: viewer.entered === true, sponsor: viewer.sponsor === true } : undefined,
      };
    });
}

// One read per mount; filtering happens in the browser over this list.
export function useContests(): { state: ContestsState; retry: () => void } {
  const [state, setState] = useState<ContestsState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ status: "loading" });
    fetch(CONTESTS_URL, {
      method: "GET",
      credentials: "same-origin",
      signal: ctrl.signal,
      headers: { Accept: "application/json" },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`contests ${res.status}`))))
      .then((raw) => setState({ status: "ready", contests: normalizeContests(raw) }))
      .catch(() => {
        if (!ctrl.signal.aborted) setState({ status: "error" });
      });
    return () => ctrl.abort();
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, retry };
}
